import type { ClassificationLevel, ProjectDetail, ProjectSettingResponse } from "@esi/shared";
import { CornerLeftUp, Lock, PenLine } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { t as translate } from "../../i18n/i18n.ts";
import { type ProjectSettingsGroup, projectSettingsPath } from "../../router.tsx";
import { SettingsGroupTabs } from "../../settings-shell/SettingsGroupTabs.tsx";
import { type SearchEntry, SettingsSearch } from "../../settings-shell/SettingsSearch.tsx";
import { SettingsGroupPage, SettingsShell } from "../../settings-shell/SettingsShell.tsx";
import { SummaryLines } from "../../settings-shell/SummaryLines.tsx";
import { settingHash, useGroupFocus } from "../../settings-shell/setting-anchors.ts";
import { useFirstGroupRedirect } from "../../settings-shell/use-first-group-redirect.ts";
import { can } from "../capabilities.ts";
import { useProjectSettings } from "../queries.ts";
import { AccessRequestsSection, useAccessRequests } from "./AccessRequestsSection.tsx";
import {
  BehaviourSection,
  behaviourRows,
  behaviourText,
  describe,
  rowsOf,
  schemeOf,
} from "./BehaviourSection.tsx";
import { BEHAVIOUR, type BehaviourDef } from "./behaviour.ts";
import { DangerZone } from "./DangerZone.tsx";
import { GeneralSection } from "./GeneralSection.tsx";
import { MembersSection } from "./MembersSection.tsx";
import {
  BEHAVIOUR_PAGES,
  type BehaviourPage,
  groupOfAnchor,
  isBehaviourPage,
  isSetForProject,
  pageOf,
  visibleGroups,
} from "./pages.ts";

/**
 * A project's settings (spec `project-settings` "Project settings sub-pages"), one page per group
 * at `/projects/:id/settings/<group>`: General, Members & access (with the access requests
 * waiting for the project's approval), Assistant, Knowledge, Web and, for the owner, the danger
 * zone. The owner and project managers edit; contributors and readers see the same rows
 * read-only; guests get the not-found page; a frozen project is read-only for everyone. What the
 * caller may change comes from their capabilities, and the server stays the authority. Without a
 * group, or on one the caller cannot see, a group opens in place (former anchors map to theirs).
 * ProjectHome provides the loaded, permitted project and owns the shell and scroll body.
 */
export function ProjectSettings({
  project,
  group,
}: {
  project: ProjectDetail;
  group: ProjectSettingsGroup | null;
}) {
  const groups = visibleGroups(project);
  const shown = group && groups.includes(group) ? group : null;
  useFirstGroupRedirect({
    group: shown,
    first: "general",
    groupOfKey: pageOf,
    pathOf: (g) => projectSettingsPath(project.id, g),
    groupOfAnchor: (hash) => {
      const g = groupOfAnchor(hash);
      return g && groups.includes(g) ? g : null;
    },
  });
  return shown ? <SettingsPages project={project} group={shown} groups={groups} /> : null;
}

/** A setting's page with its anchor. */
const settingPath = (projectId: string, key: string) =>
  `${projectSettingsPath(projectId, pageOf(key))}${settingHash(key)}`;

const groupLabel = (g: ProjectSettingsGroup) => translate(`settings.pages.${g}.label`);

function SettingsPages(props: {
  project: ProjectDetail;
  group: ProjectSettingsGroup;
  groups: readonly ProjectSettingsGroup[];
}) {
  const { project, group } = props;
  const { t, i18n } = useTranslation();
  useDocumentTitle(`${groupLabel(group)} · ${t("settings.title")} · ${project.name}`);
  const frozen = project.frozenAt !== null;
  const mayEdit = can(project, "project.edit");
  const editable = mayEdit && !frozen;
  const heading = useRef<HTMLHeadingElement>(null);
  // The query the behaviour rows read (same key: no extra request), for the summary, the tab
  // counts and the search.
  const settings = useProjectSettings(project.id);
  const data = settings.data;
  const rows = useMemo(() => (data ? behaviourRows(data) : undefined), [data]);
  const pending = useAccessRequests(project).data?.length ?? 0;
  // A former section anchor (`#members`) names a section of this page: show it, as before.
  useEffect(() => {
    const { hash } = window.location;
    if (hash && !hash.startsWith("#setting-")) {
      document.getElementById(hash.slice(1))?.scrollIntoView?.();
    }
  }, []);
  const pick = useGroupFocus({
    group,
    // A failed list names no row: focus still follows the groups.
    settings: rows ?? (settings.isError ? [] : undefined),
    groupOf: pageOf,
    pathOf: (key) => settingPath(project.id, key),
    heading,
  });
  // Worded once per list and language, not on every keystroke.
  const { language } = i18n;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the words follow the language.
  const entries = useMemo<SearchEntry[]>(() => {
    const scheme = data ? schemeOf(data) : [];
    return (rows ?? []).map((s) => {
      const def = BEHAVIOUR[s.key] as BehaviourDef;
      return {
        key: s.key,
        ...behaviourText(def),
        group: groupLabel(pageOf(s.key)),
        section: translate(`settings.behaviour.groups.${def.group}`),
        value: describe(def, s.value, scheme),
      };
    });
  }, [data, rows, language]);
  const counts = useMemo(() => {
    const counts = new Map<ProjectSettingsGroup, number>([["members", pending]]);
    for (const s of rows ?? []) {
      if (isSetForProject(s)) {
        const g = pageOf(s.key);
        counts.set(g, (counts.get(g) ?? 0) + 1);
      }
    }
    return counts;
  }, [rows, pending]);

  return (
    <SettingsShell
      title={t("settings.title")}
      lead={t("settings.lead")}
      notices={
        frozen ? (
          <Notice>{t("project.frozen")}</Notice>
        ) : !mayEdit ? (
          <Notice>{t("settings.readOnly")}</Notice>
        ) : null
      }
      // The console's row: the search alone, half width when roomy.
      controlsFrom="lg"
      controls={
        <SettingsSearch
          entries={entries}
          onPick={pick}
          label={t("settings.pages.search.label")}
          placeholder={t("settings.pages.search.placeholder")}
        />
      }
      tabs={
        <SettingsGroupTabs
          label={t("settings.pages.navLabel")}
          groups={props.groups}
          current={group}
          href={(g) => projectSettingsPath(project.id, g)}
          groupLabel={groupLabel}
          count={(g) => counts.get(g) ?? 0}
          countLabel={(g, count) =>
            g === "members"
              ? t("settings.pages.pendingCount", { count })
              : t("settings.pages.setHereCount", { count })
          }
        />
      }
    >
      <SettingsGroupPage
        id={group}
        heading={groupLabel(group)}
        headingRef={heading}
        purpose={t(`settings.pages.${group}.purpose`)}
        summary={
          isBehaviourPage(group) && data && rows ? (
            <BehaviourSummary page={group} rows={rows} scheme={schemeOf(data)} onReveal={pick} />
          ) : null
        }
      >
        {group === "general" ? (
          <GeneralSection project={project} editable={editable} />
        ) : group === "members" ? (
          <>
            <AccessRequestsSection project={project} />
            <MembersSection project={project} />
          </>
        ) : group === "danger" ? (
          <DangerZone project={project} frozen={frozen} />
        ) : (
          <BehaviourSection project={project} editable={editable} groups={BEHAVIOUR_PAGES[group]} />
        )}
      </SettingsGroupPage>
    </SettingsShell>
  );
}

/**
 * What a behaviour page holds for this project, in a line or two: the settings set for it and
 * those locked above, with their values, each a link to its row; or that all are inherited.
 */
function BehaviourSummary(props: {
  page: BehaviourPage;
  rows: readonly ProjectSettingResponse[];
  scheme: readonly ClassificationLevel[];
  onReveal: (key: string) => void;
}) {
  const { t } = useTranslation();
  const { scheme } = props;
  const rows = BEHAVIOUR_PAGES[props.page].flatMap((block) => rowsOf(props.rows, block));
  if (rows.length === 0) {
    return <p className="text-sm text-fg-secondary">{t("settings.pages.empty")}</p>;
  }
  const items = (list: readonly ProjectSettingResponse[]) =>
    list.map((s) => {
      const def = BEHAVIOUR[s.key] as BehaviourDef;
      return { key: s.key, label: behaviourText(def).label, value: describe(def, s.value, scheme) };
    });
  return (
    <SummaryLines
      lines={[
        {
          id: "setHere",
          icon: PenLine,
          lead: t("settings.pages.summary.setHere"),
          items: items(rows.filter(isSetForProject)),
        },
        {
          id: "lockedAbove",
          icon: Lock,
          lead: t("settings.pages.summary.lockedAbove"),
          items: items(rows.filter((s) => s.locked)),
        },
      ]}
      empty={{ icon: CornerLeftUp, text: t("settings.pages.summary.allInherited") }}
      onReveal={props.onReveal}
    />
  );
}
